import React, { useState, useEffect } from "react";
import { StyleSheet, View } from "react-native";
import { useLocalSearchParams, useRouter, useNavigation } from "expo-router";
import { useAuth } from "@components/auth/useAuth";

import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedProgressBar } from "@/components/ThemedProgressBar";

import { useFirebase } from "@components/firebase/useFirebase";
import { getMemory, editMemory, deleteMemory } from "@/services/firebase/memory-functions";
import { confirmEntityDeletion } from "@/services/firebase/delete-confirmation";
import { share, unshare, getSharesForSource } from "@/services/firebase/share-functions";
import { getMyContacts } from "@/services/firebase/user-functions";
import { getMyGroups } from "@/services/firebase/group-functions";
import { ShareModel, Contact, GroupModel, MemoryModel } from "common247";
import { MemoryEditor } from "@/components/memory/MemoryEditor";
import { ThemedKebabMenu, IKebabActionItem } from "@/components/ThemedKebabMenu";
import { navigateBackOrToList } from "@/utils/navigation";
import { sendMemoryTo } from "@/services/memory-pdf-exporter";
import { entitySync } from "@/services/sync/entity-sync";
import { entityCache } from "@/services/sync/entity-cache";

export default function MemoryDetailsScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const services = useFirebase();
  const { user } = useAuth();
  const router = useRouter();
  const navigation = useNavigation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [memory, setMemory] = useState<MemoryModel | null>(null);
  const [shares, setShares] = useState<ShareModel[]>([]);

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [groups, setGroups] = useState<GroupModel[]>([]);

  const fetchAllData = async () => {
    if (!services || !id) return;
    setLoading(true);
    try {
      const [memRes, sharesRes, contactsRes, groupsRes] = await Promise.all([
        getMemory(services, { memoryId: id }),
        getSharesForSource(services, { sourceId: id, sourceType: "memory" }),
        getMyContacts(services, { includeInactive: false }),
        getMyGroups(services, {}),
      ]);

      if (memRes.status) {
        setMemory(memRes.memory);
      } else {
        CustomAlert.alert("Error", memRes.error || "Memory not found.");
        navigateBackOrToList(router, "/(tabs)/my-memories", navigation);
      }

      if (sharesRes.status) setShares(sharesRes.shares);
      if (contactsRes.status) {
        setContacts(
          contactsRes.contacts.filter(
            (c) => c.id !== user?.uid && (!user?.uid || c.userId !== user.uid) && (!user?.email || c.email !== user.email),
          ),
        );
      }
      if (groupsRes.status) setGroups(groupsRes.groups);
    } catch (error) {
      console.error("Error fetching memory details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [id, services]);

  const handleSave = async (formData: any) => {
    if (!services || !id) return;
    setSaving(true);
    try {
      const res = await editMemory(services, { memoryId: id, ...formData });
      if (res.status) {
        const updatedMediaItems = formData.media
          ? Object.keys(formData.media).map((mId) => {
              const existing = (memory?.mediaItems || []).find((it: any) => (it.id || it.mediaId) === mId);
              if (existing) return existing;
              const cached = entityCache.getCachedMedia(mId);
              return {
                id: mId,
                mediaId: mId,
                title: formData.media[mId]?.displayName || cached?.title || "Media",
                downloadUrl: cached?.downloadUrl || cached?.thumbnailUrl || cached?.url,
                thumbnailUrl: cached?.thumbnailUrl || cached?.downloadUrl || cached?.url,
                ...(cached || {}),
              };
            })
          : memory?.mediaItems;

        const updatedMemory: MemoryModel = {
          ...(memory || ({} as MemoryModel)),
          ...formData,
          mediaItems: updatedMediaItems,
          id,
        };
        // Auto-save updates in place — do not close the edited item
        setMemory((prev) => (prev ? { ...prev, ...formData, mediaItems: updatedMediaItems } : updatedMemory));
        entitySync.emit({
          type: "memory",
          action: "update",
          id,
          data: updatedMemory,
        });
      } else {
        CustomAlert.alert("Error", res.error || "Failed to update memory");
      }
    } catch (e) {
      CustomAlert.alert("Error", "Unexpected error while saving");
    } finally {
      setSaving(false);
    }
  };

  const handleShare = async (targetId: string, targetType: "contact" | "group", role: "viewer" | "editor") => {
    if (!services || !id) return;
    try {
      const res = await share(services, {
        sourceId: id,
        sourceType: "memory",
        targetId,
        targetType,
        role,
      });
      if (res.status) {
        // Refresh shares
        const sharesRes = await getSharesForSource(services, { sourceId: id, sourceType: "memory" });
        if (sharesRes.status) setShares(sharesRes.shares);
      } else {
        CustomAlert.alert("Error", res.error || "Failed to share");
      }
    } catch (e) {
      CustomAlert.alert("Error", "Unexpected error");
    }
  };

  const handleUnshare = async (shareId: string) => {
    if (!services || !id) return;
    try {
      const res = await unshare(services, { shareId });
      if (res.status) {
        const sharesRes = await getSharesForSource(services, { sourceId: id, sourceType: "memory" });
        if (sharesRes.status) setShares(sharesRes.shares);
      } else {
        CustomAlert.alert("Error", res.error || "Failed to remove share.");
      }
    } catch (e) {
      CustomAlert.alert("Error", "Unexpected error.");
    }
  };

  const handleSendTo = async () => {
    if (!memory) return;
    try {
      await sendMemoryTo(memory);
    } catch (e: any) {
      CustomAlert.alert("Send To", e?.message || "Failed to export or send memory.");
    }
  };

  /**
   * Prompts confirmation and deletes the current memory.
   */
  const handleDelete = async () => {
    if (!services || !id) return;
    await confirmEntityDeletion({
      services,
      entityType: "memory",
      entityId: id,
      entityName: memory?.title,
      onProceedDelete: async (cascadeUnlink) => {
        setIsDeleting(true);
        try {
          const res = await deleteMemory(services, { memoryId: id, cascadeUnlink });
          if (!res.status) {
            setIsDeleting(false);
          }
          return res;
        } catch (error) {
          setIsDeleting(false);
          throw error;
        }
      },
      onSuccess: () => {
        navigateBackOrToList(router, "/(tabs)/my-memories", navigation);
      },
      onError: () => {
        setIsDeleting(false);
      },
    });
  };

  const userRole = memory?.userRole || "owner";
  const isOwner = userRole === "owner";
  const canEdit = isOwner || userRole === "editor";
  const isViewer = userRole === "viewer";

  const kebabActions: IKebabActionItem[] = [];
  if (memory) {
    kebabActions.push({
      id: "sendTo",
      label: "Send To",
      icon: "paper-plane-outline",
      onPress: handleSendTo,
    });
  }
  if (memory && canEdit) {
    kebabActions.push({
      id: "delete",
      label: "Delete Memory",
      icon: "trash-outline",
      destructive: true,
      onPress: handleDelete,
      disabled: isDeleting,
    });
  }

  return (
    <ThemedScreen
      title={memory ? `${isViewer ? "View" : "Edit"}: ${memory.title}` : "Memory Details"}
      loading={loading}
      rightComponent={kebabActions.length > 0 ? <ThemedKebabMenu actions={kebabActions} accessibilityLabel="Memory options" /> : undefined}
    >
      <View style={styles.container}>
        <ThemedProgressBar loading={loading} />
        {!loading && memory && (
          <MemoryEditor
            memory={memory}
            shares={shares}
            contacts={contacts}
            groups={groups}
            isSaving={saving}
            isLoadingShares={false}
            onSave={handleSave}
            onShare={handleShare}
            onUnshare={handleUnshare}
            onSendTo={handleSendTo}
            onDelete={canEdit ? handleDelete : undefined}
            isDeleting={isDeleting}
            onCancel={() => navigateBackOrToList(router, "/(tabs)/my-memories", navigation)}
          />
        )}
      </View>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  center: { flex: 1, justifyContent: "center", alignItems: "center" },
  container: { flex: 1, padding: 16 },
});
